import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { artistMatchesAgeFilter } from '../utils/artistAge';

const ArtistSalonAgeFilterContext = createContext(null);

export function submissionArtistUid(submission) {
  if (!submission) return null;
  return submission.uid || submission.userId || null;
}

function profilesIncludeBirthdayField(list) {
  return Array.isArray(list) && list.length > 0 && list.every((p) => Object.prototype.hasOwnProperty.call(p, 'birthday'));
}

export function ArtistSalonAgeFilterProvider({ profiles: externalProfiles = [], children }) {
  const [ageFilter, setAgeFilter] = useState('all');
  const [profiles, setProfiles] = useState(externalProfiles);

  useEffect(() => {
    if (profilesIncludeBirthdayField(externalProfiles)) {
      setProfiles(externalProfiles);
    }
  }, [externalProfiles]);

  useEffect(() => {
    if (profilesIncludeBirthdayField(profiles)) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const snapshot = await getDocs(collection(db, 'artistProfiles'));
        if (cancelled) return;
        const loaded = snapshot.docs.map((profileDoc) => {
          const data = profileDoc.data();
          return {
            uid: data.uid || profileDoc.id,
            birthday: data.birthday || '',
            ...data,
          };
        });
        setProfiles(loaded);
      } catch (err) {
        console.warn('[ArtistSalonAgeFilter] Failed to load artist birthdays:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profiles]);

  const profilesByUid = useMemo(() => {
    const map = new Map();
    (profiles || []).forEach((profile) => {
      const uid = profile?.uid || profile?.id;
      if (uid) map.set(uid, profile);
    });
    return map;
  }, [profiles]);

  const uidMatchesAgeFilter = useCallback(
    (uid) => {
      if (!ageFilter || ageFilter === 'all') return true;
      if (!uid) return false;
      const profile = profilesByUid.get(uid);
      return artistMatchesAgeFilter(profile || {}, ageFilter);
    },
    [ageFilter, profilesByUid]
  );

  const filterSubmissions = useCallback(
    (submissions) => {
      if (!Array.isArray(submissions) || ageFilter === 'all') return submissions || [];
      return submissions.filter((s) => uidMatchesAgeFilter(submissionArtistUid(s)));
    },
    [ageFilter, uidMatchesAgeFilter]
  );

  const value = useMemo(
    () => ({
      ageFilter,
      setAgeFilter,
      uidMatchesAgeFilter,
      filterSubmissions,
      profilesByUid,
    }),
    [ageFilter, uidMatchesAgeFilter, filterSubmissions, profilesByUid]
  );

  return (
    <ArtistSalonAgeFilterContext.Provider value={value}>
      {children}
    </ArtistSalonAgeFilterContext.Provider>
  );
}

export function useArtistSalonAgeFilter() {
  const ctx = useContext(ArtistSalonAgeFilterContext);
  if (!ctx) {
    return {
      ageFilter: 'all',
      setAgeFilter: () => {},
      uidMatchesAgeFilter: () => true,
      filterSubmissions: (subs) => subs || [],
      profilesByUid: new Map(),
    };
  }
  return ctx;
}
